import axios from "axios";
import { Axios } from "../../Api/axios";

// Live gifts — backend: Admin\Gifts\AdminGiftsController (Route::resource
// '/Admin-Gifts'). A gift has an English `name` plus optional names in the
// other app languages, sent as flat name_ar / name_es / name_pt / name_ru /
// name_zh fields and read back as `name_translations` ({en, ar, ...}).
// The app shows the gift name in the viewer's language, falling back to the
// English name when a translation is empty.
//
// Files have no size limit: video-worker optimises every gift file after the
// upload (video, GIF, icon) and warms the CDN.

export const GIFT_LANGS = [
  { code: "ar", label: "Arabic", native: "العربية", dir: "rtl" },
  { code: "es", label: "Spanish", native: "Español", dir: "ltr" },
  { code: "pt", label: "Portuguese", native: "Português", dir: "ltr" },
  { code: "ru", label: "Russian", native: "Русский", dir: "ltr" },
  { code: "zh", label: "Chinese", native: "中文", dir: "ltr" },
];

export const IMG_ACCEPT = "image/png,image/jpeg";
export const ANIM_ACCEPT = "video/mp4,image/gif,audio/aac,audio/mp4,.m4a,.aac";

// The API runs on Cloud Run, which refuses a request body over 32 MiB. The
// multipart fallback stays under that; anything bigger has to go direct.
const MULTIPART_MAX_BYTES = 30 * 1024 * 1024;

const TYPE_EXT = {
  "image/png": "png",
  "image/jpeg": "jpg",
  "video/mp4": "mp4",
  "image/gif": "gif",
  "audio/aac": "aac",
  "audio/mp4": "m4a",
  "audio/x-m4a": "m4a",
};

const extOf = (file) => {
  const m = /\.([a-z0-9]+)$/i.exec(file.name || "");
  return (m ? m[1] : TYPE_EXT[file.type] || "").toLowerCase();
};

export const listGifts = () => Axios.get("/Admin-Gifts").then((r) => r.data?.data || []);

export const deleteGift = (id) => Axios.delete(`/Admin-Gifts/${id}`).then((r) => r.data);

/// Uploads one file straight to the bucket with a signed URL (no size cap).
/// Plain axios on purpose: the signed URL is the credential, and the API's
/// Bearer header would break the signature.
async function uploadDirect(file, kind, onLoaded) {
  const res = await Axios.post("/Admin-Gifts/upload-url", { kind, extension: extOf(file) });
  const { upload_url: url, object_name: objectName, headers } = res.data?.data || {};
  if (!url || !objectName) throw new Error("No upload URL from the server");
  await axios.put(url, file, {
    headers,
    onUploadProgress: (e) => onLoaded(e.loaded || 0),
  });
  return objectName;
}

/// Creates (id = null) or updates a gift.
///
/// New files go straight to storage first. If that is not possible (the
/// bucket does not allow this site yet, or the API is older), the files are
/// sent inside the form instead — fine up to ~30 MB together.
/// `onProgress(0..1)` follows the file upload.
export async function saveGift(id, { name, coins, translations, img, svg }, onProgress = () => {}) {
  const files = [
    ["img", img],
    ["svg", svg],
  ].filter(([, f]) => f);
  const totalBytes = files.reduce((s, [, f]) => s + f.size, 0);

  let objects = null;
  if (files.length) {
    const loaded = {};
    try {
      objects = {};
      for (const [kind, file] of files) {
        objects[kind] = await uploadDirect(file, kind, (n) => {
          loaded[kind] = n;
          onProgress(Object.values(loaded).reduce((a, b) => a + b, 0) / (totalBytes || 1));
        });
      }
    } catch (e) {
      objects = null;
      if (totalBytes > MULTIPART_MAX_BYTES) {
        const why = e?.response?.status ? `HTTP ${e.response.status}` : e?.message || "network error";
        throw new Error(
          `Uploading straight to storage failed (${why}). Files over 30 MB can only be uploaded that way — ` +
            "the storage bucket must allow this dashboard (CORS)."
        );
      }
      onProgress(0);
    }
  }

  const fd = new FormData();
  if (id) fd.append("_method", "PUT"); // PHP does not parse files on a real PUT
  fd.append("name", name.trim());
  fd.append("coins", String(coins).trim());
  // Always send every language: an empty field clears that translation.
  GIFT_LANGS.forEach(({ code }) => fd.append(`name_${code}`, (translations[code] || "").trim()));
  if (objects) {
    Object.entries(objects).forEach(([kind, objectName]) => fd.append(`${kind}_object`, objectName));
  } else {
    files.forEach(([kind, file]) => fd.append(kind, file));
  }

  const res = await Axios.post(id ? `/Admin-Gifts/${id}` : "/Admin-Gifts", fd, {
    onUploadProgress: objects || !files.length ? undefined : (e) => onProgress((e.loaded || 0) / (e.total || 1)),
  });
  return res.data;
}

/// Kind of preview element a gift animation URL / file needs.
export const animKind = (urlOrType = "") => {
  const v = String(urlOrType).toLowerCase().split("?")[0];
  if (v.includes("gif")) return "image";
  if (/(aac|m4a|audio)/.test(v)) return "audio";
  return "video";
};

/// "12.4 MB"
export const fileSize = (bytes = 0) =>
  bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
